import React from 'react'
import {
  Card,
  CardContent,
  Typography,
  Divider,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Box,
  Collapse,
  Link,
  Alert,
  Autocomplete,
  CircularProgress,
} from '@mui/material'

const OnPremConfigurationStep = ({
  formData,
  setFormData,
  isComplete,
  itsmProducts,
  loadingProducts,
  selectedOnpremProduct,
  handleOnpremProductChange,
  onpremProductExists,
  productSearchText,
  setProductSearchText,
  setProductOnboardModalOpen,
  stepNumber = 3,
}) => {
  return (
    <Collapse in={formData.isAWS === 'false'}>
      <Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip 
              label={isComplete ? "✓" : String(stepNumber)} 
              color={isComplete ? "success" : "primary"} 
              size="small" 
            />
            On-Premises Configuration
          </Typography>
          <Divider sx={{ my: 2 }} />
          
          <Grid container spacing={3}>
            {/* For pre-existing certs, product and environment are already set from Venafi Lookup */}
            {formData.preExisting === 'true' ? (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Product Name"
                    value={formData.productName || formData.productCode || '—'}
                    InputProps={{ readOnly: true }}
                    helperText="Set from Venafi Certificate Lookup"
                    disabled
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Environment"
                    value={formData.environment ? formData.environment.toUpperCase() : '—'}
                    InputProps={{ readOnly: true }}
                    helperText="Set from Venafi Certificate Lookup"
                    disabled
                  />
                </Grid>

                {/* Block progress if the ITSM product has not been onboarded (no DLs registered) */}
                {onpremProductExists === false && (
                  <Grid item xs={12}>
                    <Alert
                      severity="error"
                      sx={{ alignItems: 'flex-start' }}
                    >
                      <Typography variant="body2" fontWeight={600} gutterBottom>
                        ITSM product not onboarded
                      </Typography>
                      <Typography variant="body2">
                        The product <strong>{formData.productName || formData.productCode}</strong> has
                        not been registered in CertManage yet — no distribution lists are on file.
                        Please go to the{' '}
                        <Link
                          component="button"
                          variant="body2"
                          onClick={() => setProductOnboardModalOpen(true)}
                          sx={{ fontWeight: 600, textDecoration: 'underline', cursor: 'pointer' }}
                        >
                          Onboarding ITSM Products
                        </Link>{' '}
                        tab, register the product and its team distribution lists, then return here to continue.
                      </Typography>
                    </Alert>
                  </Grid>
                )}
              </>
            ) : (
              <>
                <Grid item xs={12}>
                  <Autocomplete
                    fullWidth
                    options={itsmProducts}
                    getOptionLabel={(option) => option?.product_name || ''}
                    value={selectedOnpremProduct}
                    onChange={(event, newValue) => handleOnpremProductChange(newValue)}
                    onInputChange={(event, newInputValue, reason) => {
                      if (reason === 'input') {
                        setProductSearchText(newInputValue)
                      }
                    }}
                    loading={loadingProducts}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Product Name *"
                        placeholder="Search and select product"
                        helperText="Search for your ITSM product"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingProducts ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} key={option.product_id}>
                        <Box>
                          <Typography variant="body1">{option.product_name}</Typography>
                          {option.product_code && (
                            <Typography variant="caption" color="text.secondary">
                              Code: {option.product_code}
                            </Typography>
                          )}
                        </Box>
                      </li>
                    )}
                  />
                  {selectedOnpremProduct && onpremProductExists === false && (
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'warning.lighter', borderRadius: 1, border: '1px solid', borderColor: 'warning.main' }}>
                      <Typography variant="body2" color="warning.dark" gutterBottom>
                        Product has not been onboarded yet.
                      </Typography>
                      <Link
                        component="button"
                        variant="body2"
                        onClick={() => setProductOnboardModalOpen(true)}
                        sx={{ fontWeight: 600, color: 'primary.main', textDecoration: 'underline', cursor: 'pointer' }}
                      >
                        Do you want to onboard it now?
                      </Link>
                    </Box>
                  )}
                </Grid>

                {selectedOnpremProduct && onpremProductExists === true && (
                  <>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel>SDLC Environment *</InputLabel>
                        <Select
                          value={formData.environment}
                          onChange={(e) => setFormData({ ...formData, environment: e.target.value, venafiPolicyBase: '' })}
                          label="SDLC Environment *"
                        >
                          <MenuItem value="dev">Development</MenuItem>
                          <MenuItem value="qa">QA</MenuItem>
                          <MenuItem value="uat">UAT</MenuItem>
                          <MenuItem value="prd">Production</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </>
                )}
              </>
            )}
          </Grid>

          {/* Device info note — always show for pre-existing; for new certs only when product is onboarded */}
          {(formData.preExisting === 'true' || (selectedOnpremProduct && onpremProductExists === true)) && (
            <>
              <Divider sx={{ my: 3 }} />
              <Box sx={{ p: 2, bgcolor: 'info.lighter', borderRadius: 1, border: '1px solid', borderColor: 'info.main' }}>
                <Typography variant="body2" color="info.dark">
                  ℹ️ Device information (load balancers and servers) will be automatically retrieved from Venafi based on the certificate.
                </Typography>
              </Box>
            </>
          )}
        </CardContent>
      </Card>
    </Collapse>
  )
}

export default OnPremConfigurationStep
